import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MarginCalculatorMarginService } from '../../margin-calculator/services/margin-calculator-margin.service';
import { WeeklyPayResponseModel } from '../quick-quote/services/quick-quote.service';
import { DirectOfferMarginService } from './direct-offer-margin.service';
import { DirectOfferItem } from './direct-offer-create-margin-item.model';
import { DirectOfferMarginModel } from './direct-offer-create-margin.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
    selector: 'nova-direct-offer-create-margin-dialog',
    templateUrl: './direct-offer-create-margin-dialog.component.html',
    styleUrls: ['./direct-offer-create-margin-dialog.component.scss']
})
export class DirectOfferCreateMarginDialogComponent implements OnInit {
    tableDisplaying = false;
    isSaving = false;
    offers: DirectOfferItem[] = [];
    selectedJob: any;
    calcBtnLocked = true;

    insuranceFormCtrl = new UntypedFormControl(false);

    private _needsInsurance = false;

    private _directOfferQuote: DirectOfferMarginModel;
    private readonly _refreshWeeklyPay = new Subject();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly _dialogRef: MatDialogRef<DirectOfferCreateMarginDialogComponent>,
        private readonly _directOfferQuoteService: DirectOfferMarginService,
        private readonly _marginService: MarginCalculatorMarginService,
        private readonly _toasterService: ToasterService
    ) {}

    ngOnInit(): void {
        if (this.data && this.data.selectedJob) {
            this._marginService
                .getTargetMargin(this.data.selectedJob.facilityId, this.data.selectedJob.id)
                .pipe(untilDestroyed(this))
                .subscribe((margin: number) => {
                    this.data.selectedJob['targetMargin'] = margin;
                    this.data.selectedJob['marginPct'] = margin;
                    this.offers = [this.data.selectedJob];
                });
        }

        this.insuranceFormCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (this._needsInsurance !== value) {
                this._needsInsurance = value;
            }

            this._refreshWeeklyPay.next(this._buildDirectOfferRequest());
        });

        this._refreshWeeklyPay
            .pipe(
                switchMap(() => {
                    this._buildDirectOfferRequest();
                    this.calcBtnLocked = true;
                    return this._directOfferQuoteService.getWeeklyPayQuote(this._directOfferQuote, this.data.offerId);
                }),
                untilDestroyed(this)
            )
            .subscribe((value: WeeklyPayResponseModel) => {
                this.offers[0]['nonTaxable'] = value.nonTaxableWeeklyPay;
                this.offers[0]['nonTaxableAltPay'] = value.nonTaxableAltWeeklyPay;
                this.offers[0]['housingCost'] = value.housingCost;
                this.offers[0]['hasGsaRates'] = value.hasGsaRates;
                this.offers[0]['hasStipendExcess'] = value.hasStipendExcess;
                this.offers[0]['weeklyPay'] = value.weeklyPay;
                this.offers[0]['altWeeklyPay'] = value.altWeeklyPay;
                this.offers[0]['taxable'] = value.taxableWeeklyPay;
                this.offers[0]['taxableAltPay'] = value.taxableAltWeeklyPay;
                this.calcBtnLocked = false;
            });
    }

    closeDialog(): void {
        this._dialogRef.close();
    }

    saveMargin(): void {
        if (!this.isSaving) {
            this.isSaving = true;

            this._saveMargin();
        }
    }

    quotesUpdated(jobs: DirectOfferItem[]): void {
        this.selectedJob = jobs[0];
        this._refreshWeeklyPay.next(this._buildDirectOfferRequest());
    }

    private _buildDirectOfferRequest() {
        this._directOfferQuote = {
            recruiterId: this.data.recruiterId,
            candidateId: this.data.candidateId,
            marginPercent: this.selectedJob['marginPct'] || 0,
            targetMargin: this.selectedJob['targetMargin'],
            isAyaHousing: this.selectedJob['ayaHousing'],
            isInsurance: this._needsInsurance,
            jobId: this.selectedJob.id,
            relocation: this.selectedJob['relocation'] || 0
        };
    }

    private _saveMargin(): void {
        this._directOfferQuoteService
            .createDirectOfferMargin(this._directOfferQuote, this.data.offerId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (marginId: number) => {
                    this._toasterService.toast('Offer Margin has been created successfully');
                    this._dialogRef.close(marginId);
                },
                error: (e) => {
                    this._toasterService.toast(`Error occurred while creating Offer Margin: ${e.error}`, 7000);
                    this._dialogRef.close();
                }
            });
    }
}
